// src/components/Warehouse/WarehouseItemList.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import api from "services/api";
import { useSnackbar } from "context/SnackbarContext";
import { useCompany } from "context/CompanyContext";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import MDButton from "components/MDButton";
import warehouseTableData from "./warehouseTableData";
import Loader from "components/Loader/Loader";
import WarehouseItemModal from "./WarehouseItemModal";
import { useLanguage } from "context/LanguageContext";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";

const WarehouseItemList = ({ refreshTrigger, onRefreshComplete }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedWarehouseCode, setSelectedWarehouseCode] = useState("");
  const [warehouseCodes, setWarehouseCodes] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { selectedCompany } = useCompany();
  const showSnackbar = useSnackbar();
  const { t } = useLanguage();
  const theme = useTheme();
  const refreshCompleteRef = useRef(onRefreshComplete);

  useEffect(() => {
    refreshCompleteRef.current = onRefreshComplete;
  }, [onRefreshComplete]);

  const handleSuccess = () => {
    fetchWarehouseItems(pageIndex, pageSize);
  };

  /**
   * Fetches warehouse items from the backend.
   */
  const fetchWarehouseItems = useCallback(
    async (currentPage, currentSize) => {
      if (!selectedCompany) return;
      setLoading(true);

      const apiUrl = `/api/warehouse/items/?company_id=${selectedCompany.id}&page=${
        currentPage + 1
      }&page_size=${currentSize}&search=${encodeURIComponent(
        searchQuery
      )}&warehouse_code=${encodeURIComponent(selectedWarehouseCode)}`;
      console.log("Fetching warehouse items from:", apiUrl);

      let succeeded = false;
      try {
        const response = await api.get(apiUrl);
        const result = response.data;

        if (result.success) {
          const warehouseItemsWithId = result.items.map((item) => ({
            ...item,
            id: item.id,
          }));
          console.log("Fetched Warehouse Items:", warehouseItemsWithId);
          setRows(warehouseItemsWithId);
          setPageCount(Math.ceil(result.total_items / currentSize));
          setTotalItems(result.total_items);
          const returnedWarehouseCodes = Array.isArray(result.warehouse_codes)
            ? result.warehouse_codes.filter(Boolean)
            : [];
          const rowWarehouseCodes = [
            ...new Set(warehouseItemsWithId.map((item) => item.warehouse_code).filter(Boolean)),
          ];
          setWarehouseCodes(
            returnedWarehouseCodes.length ? returnedWarehouseCodes : rowWarehouseCodes
          );
          succeeded = true;
        } else {
          showSnackbar(`Error: ${result.error}`, "error");
        }
      } catch (error) {
        console.error("Error fetching warehouse items:", error);
        showSnackbar(
          t("errorFetchingWarehouseItems") || "Error fetching warehouse items.",
          "error"
        );
      } finally {
        setLoading(false);
        refreshCompleteRef.current?.(succeeded);
      }
    },
    [selectedCompany, showSnackbar, t, searchQuery, selectedWarehouseCode]
  );

  const handleEdit = useCallback((item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  }, []);

  /**
   * Handles deletion of a warehouse item.
   * @param {number} itemId - The ID of the item to delete.
   */
  const handleDelete = useCallback(
    async (itemId) => {
      try {
        const response = await api.post(
          `/api/warehouse/delete/`,
          {
            item_id: itemId,
            company_id: selectedCompany.id,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          showSnackbar(t("warehouseItemDeletedSuccessfully"), "success");
          setItemToDelete(null);
          fetchWarehouseItems(pageIndex, pageSize);
        } else {
          showSnackbar(`Error: ${response.data.error}`, "error");
        }
      } catch (error) {
        console.error("Error deleting warehouse item:", error);
        showSnackbar(t("errorDeletingWarehouseItem") || "Error deleting warehouse item.", "error");
      }
    },
    [selectedCompany, showSnackbar, pageIndex, pageSize, fetchWarehouseItems]
  );

  /**
   * Fetch warehouse items when the selected company or refreshTrigger changes.
   */
  useEffect(() => {
    if (selectedCompany) {
      fetchWarehouseItems(pageIndex, pageSize);
    }
  }, [selectedCompany, fetchWarehouseItems, refreshTrigger, pageIndex, pageSize]);

  // Define table columns
  const columns = useMemo(
    () => warehouseTableData(setItemToDelete, handleEdit).columns,
    [handleEdit]
  );
  const data = useMemo(() => rows, [rows]);

  // Initialize the table instance
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { pageIndex: controlledPageIndex, pageSize: controlledPageSize },
    gotoPage,
    setPageSize: setControlledPageSize,
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: pageCount,
      initialState: { pageIndex, pageSize },
    },
    useSortBy,
    usePagination,
    useRowSelect
  );

  useEffect(() => {
    setPageIndex(controlledPageIndex);
    setControlledPageSize(pageSize);
  }, [controlledPageIndex, pageSize, setControlledPageSize]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  /**
   * Handles changes to the number of items displayed per page.
   * @param {object} event - The event object.
   */
  const handleItemsPerPageChange = (event) => {
    const newPageSize = Number(event.target.value);
    setPageSize(newPageSize);
    gotoPage(0);
  };

  const handlePageChange = (event, value) => {
    gotoPage(value - 1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    gotoPage(0);
  };

  const handleWarehouseFilterChange = (event) => {
    setSelectedWarehouseCode(event.target.value);
    gotoPage(0);
  };

  if (!selectedCompany) {
    return <Alert severity="info">{t("selectCompanyFirst")}</Alert>;
  }

  if (loading && !rows.length) return <Loader />;

  return (
    <>
      <MDBox p={{ xs: 2, md: 3 }}>
        <MDBox display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
          <TextField
            label="Ieškoti prekių"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Kodas, pavadinimas ar grupė"
            size="small"
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="warehouse-filter-label">Sandėlis</InputLabel>
            <Select
              labelId="warehouse-filter-label"
              value={selectedWarehouseCode}
              label="Sandėlis"
              onChange={handleWarehouseFilterChange}
            >
              <MenuItem value="">Visi sandėliai</MenuItem>
              {warehouseCodes.map((warehouseCode) => (
                <MenuItem key={warehouseCode} value={warehouseCode}>
                  {warehouseCode}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MDBox fontWeight="medium">Prekių: {totalItems}</MDBox>
        </MDBox>

        {/* eslint-disable react/jsx-props-no-spreading */}
        <MDBox sx={{ overflowX: "auto" }}>
          <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              {headerGroups.map((headerGroup, index) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id || index}>
                  {headerGroup.headers.map((column, colIndex) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={column.id || colIndex}
                      style={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        background: theme.palette.action.hover,
                        padding: "10px 12px",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: theme.palette.text.secondary,
                        textAlign: "left",
                        width: `${column.width || "20%"}`,
                      }}
                    >
                      {column.render("Header")}
                      <span>{column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}</span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{ padding: "24px 12px", textAlign: "center" }}
                  >
                    {t("noResults") || "Nėra sandėlio įrašų."}
                  </td>
                </tr>
              ) : (
                page.map((row, rowIndex) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} key={row.id || rowIndex}>
                      {row.cells.map((cell, cellIndex) => (
                        <td
                          {...cell.getCellProps()}
                          key={cell.column.id || cellIndex}
                          style={{
                            padding: "10px 12px",
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            textAlign: "left",
                            width: `${cell.column.width || "20%"}`,
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </MDBox>
        {/* eslint-enable react/jsx-props-no-spreading */}

        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={3}>
          <FormControl variant="outlined" style={{ minWidth: 120 }}>
            <InputLabel id="items-per-page-label">{t("perPage")}</InputLabel>
            <Select
              labelId="items-per-page-label"
              value={pageSize}
              onChange={handleItemsPerPageChange}
              label={t("perPage")}
            >
              {[20, 40, 100].map((pageSizeOption) => (
                <MenuItem key={pageSizeOption} value={pageSizeOption}>
                  {t("showAll")} {pageSizeOption}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Pagination
            count={pageCount}
            page={pageIndex + 1}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
          />
        </MDBox>
      </MDBox>
      <Dialog
        open={Boolean(itemToDelete)}
        onClose={() => setItemToDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("delete")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("confirmDeleteItem")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setItemToDelete(null)}>
            {t("cancel")}
          </MDButton>
          <MDButton variant="contained" color="error" onClick={() => handleDelete(itemToDelete)}>
            {t("delete")}
          </MDButton>
        </DialogActions>
      </Dialog>
      <WarehouseItemModal
        open={isModalOpen}
        handleClose={handleCloseModal}
        onSuccess={handleSuccess}
        item={editingItem}
      />
    </>
  );
};

WarehouseItemList.propTypes = {
  refreshTrigger: PropTypes.bool.isRequired, // Added prop to trigger refresh
  onRefreshComplete: PropTypes.func,
};

WarehouseItemList.defaultProps = {
  onRefreshComplete: undefined,
};

export default WarehouseItemList;
